import React from 'react';

import { createLogger } from '@campaign/utils/logger';
import { UnexpectedError } from '@campaign/view/components/UnexpectedError';

type Props = {
  children: React.ReactElement;
  onCatchError?: (error: Error) => void;
};

type State = {
  hasError: boolean;
};

const logger = createLogger('@ErrorBoundary');

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
    this.eventHandler = this.updateError.bind(this);
  }

  eventHandler: (event: PromiseRejectionEvent) => void;

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  updateError(event: PromiseRejectionEvent) {
    logger.error({ logData: 'Caught a unhandled rejection' });
    this.setState({ hasError: true });
    this.props.onCatchError?.(event.reason);
  }

  componentDidCatch(error: Error) {
    logger.error({ logData: 'Caught a uncaught error' });
    this.props.onCatchError?.(error);
  }

  componentDidMount() {
    window.addEventListener('unhandledrejection', this.eventHandler);
  }

  componentWillUnmount() {
    window.removeEventListener('unhandledrejection', this.eventHandler);
  }

  render() {
    if (this.state.hasError) {
      return <UnexpectedError />;
    }
    return this.props.children;
  }
}
